import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { medidaDoItem, qtdDoItem } from "../../lib/unidadeVenda";
import { horaDe, pedidoEm } from "./cancelarEntregue";
import { unidadesCancelaveis } from "./cancelarParte";

/** Confirma antes de cancelar um produto já enviado da mesa (dono, 25/09/2026).
 *  Com 2 ou mais unidades na linha, pergunta quantas (dono, 26/09/2026): uma,
 *  duas… ou todas. `onConfirmar(quantidade)` — a quantidade escolhida.
 *  `produtos`: cadastro, pra saber se a linha é quilo/litro (cancela inteira).
 *
 *  `contexto` (`contextoDoCancelamento`, dono 03/10/2026): produto que já foi
 *  entregue ganha o aviso — a cozinha não fica sabendo desse cancelamento — e,
 *  se o mesmo produto ainda não chegou na mesa em outra linha, a oferta de
 *  cancelar esse. `onTrocar(linha)` troca o alvo da janela; `trocado` diz que
 *  ela veio dessa troca. Nada troca sozinho: quem escolhe é a pessoa. */
export default function ConfirmarCancelarItem({ item, produtos, contexto, trocado, onTrocar, onConfirmar, onFechar }) {
  const nome = item?.name || item?.nome || "este produto";
  const total = unidadesCancelaveis(item, produtos);
  const [qtd, setQtd] = useState(1);
  useEffect(() => { setQtd(1); }, [item]);
  const todas = !total || qtd >= total;
  const entregue = !!contexto?.entregue;
  const iguais = entregue ? contexto.iguais || [] : [];
  // Até 10 unidades: um botão por quantidade; acima disso, − / +
  const botoes = total && total <= 10 ? Array.from({ length: total - 1 }, (_, i) => i + 1) : null;
  const chip = (ativo) => `h-10 min-w-[2.75rem] px-3 rounded-lg border text-sm font-semibold touch-manipulation ${
    ativo ? "bg-red-600 border-red-600 text-white" : "border-border bg-card text-foreground hover:border-red-400"}`;
  const pedidoAs = horaDe(pedidoEm(item));
  const observacao = String(item?.observation || item?.observacao || "").trim();
  return (
    <AlertDialog open={!!item} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <AlertDialogContent className="max-w-sm max-h-[90dvh] overflow-y-auto" data-e2e="confirmar-cancelar-item">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancelar {nome}?</AlertDialogTitle>
          <AlertDialogDescription>
            {total
              ? `Esta linha tem ${total} unidades. Quantas quer cancelar?`
              : entregue
                ? "O produto sai da conta da mesa."
                : "O produto sai da conta da mesa e aparece como CANCELADO na cozinha e na comanda dos garçons."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {trocado && !entregue && (
          <p className="text-xs rounded-lg bg-muted px-3 py-2" data-e2e="cancelar-trocado">
            Agora é o que ainda não foi entregue{pedidoAs ? ` (pedido das ${pedidoAs})` : ""}{observacao ? ` — obs.: ${observacao}` : ""}.
          </p>
        )}
        {entregue && (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2" data-e2e="cancelar-ja-entregue">
            <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Este produto já foi entregue{contexto.entregueAs ? ` às ${contexto.entregueAs}` : ""}.
            </p>
            <p className="text-xs text-muted-foreground mt-1">A cozinha não recebe aviso deste cancelamento: ele só sai da conta.</p>
          </div>
        )}
        {iguais.length > 0 && (
          <div className="space-y-2" data-e2e="cancelar-iguais">
            <p className="text-sm font-medium">
              {iguais.length === 1
                ? "O mesmo produto ainda não foi entregue em outro pedido da mesa. Era esse que você queria cancelar?"
                : "O mesmo produto ainda não foi entregue em outros pedidos da mesa. Era um desses que você queria cancelar?"}
            </p>
            {iguais.map((l) => (
              <div key={l.idx} className="flex items-center gap-2 rounded-lg border border-border p-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {qtdDoItem(l.item.quantity ?? l.item.quantidade ?? 1, medidaDoItem(l.item, produtos))} {nome}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[l.rotulo, l.pedidoAs && `pedido das ${l.pedidoAs}`, l.quem].filter(Boolean).join(" · ")}
                  </p>
                  {l.observacao && <p className="text-xs text-blue-600 dark:text-blue-400 truncate">Obs.: {l.observacao}</p>}
                </div>
                <button type="button" onClick={() => onTrocar?.(l)} data-e2e="cancelar-igual-na-cozinha"
                  className="h-10 px-3 shrink-0 rounded-lg border border-red-500/50 text-red-600 text-sm font-semibold hover:bg-red-500/10 touch-manipulation">
                  Cancelar este
                </button>
              </div>
            ))}
          </div>
        )}
        {total && (
          <div className="flex flex-wrap gap-2" data-e2e="cancelar-quantidade">
            {botoes ? botoes.map((n) => (
              <button key={n} type="button" className={chip(qtd === n)} onClick={() => setQtd(n)} data-e2e={`cancelar-qtd-${n}`}>{n}</button>
            )) : (
              <div className="flex items-center gap-2">
                <button type="button" className={chip(false)} onClick={() => setQtd((v) => Math.max(1, v - 1))} aria-label="Menos">−</button>
                <span className="min-w-[2.5rem] text-center text-lg font-bold tabular-nums">{Math.min(qtd, total)}</span>
                <button type="button" className={chip(false)} onClick={() => setQtd((v) => Math.min(total, v + 1))} aria-label="Mais">+</button>
              </div>
            )}
            <button type="button" className={chip(todas)} onClick={() => setQtd(total)} data-e2e="cancelar-qtd-todas">Todas ({total})</button>
          </div>
        )}
        {total && (
          <p className="text-xs text-muted-foreground">
            {todas
              ? "A linha inteira sai da conta."
              : entregue
                ? `Fica ${total - qtd} na conta.`
                : `Fica ${total - qtd} na conta; ${qtd === 1 ? "a unidade cancelada aparece" : "as canceladas aparecem"} como CANCELADO na cozinha e na comanda.`}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-e2e="confirmar-cancelar-voltar">Voltar</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirmar(todas ? null : qtd)} className="bg-red-600 hover:bg-red-700 text-white" data-e2e="confirmar-cancelar-sim">
            {/* Entregue: o próprio botão diz o que está saindo, pra quem toca direto nele */}
            {!total
              ? (entregue ? "Cancelar o já entregue" : "Cancelar produto")
              : entregue
                ? (todas ? `Cancelar as ${total} já entregues` : `Cancelar ${qtd} já ${qtd === 1 ? "entregue" : "entregues"}`)
                : todas ? `Cancelar todas (${total})` : `Cancelar ${qtd} ${qtd === 1 ? "unidade" : "unidades"}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
